import React, { useEffect, useState, useRef } from "react";
import { useLocation, Link } from "react-router-dom";
import { api, auth } from "@/lib/api";
import { useT } from "@/lib/i18n";
import { canUse, consume, getRemaining, isGuest } from "@/lib/guest";
import { Sparkles, X, Send, Loader2, Crown } from "lucide-react";
import { toast } from "sonner";

/**
 * geoVINUM AI floating action button — context-aware help on every page.
 * - Bottom-right pill button (always visible)
 * - Click → opens a chat sheet anchored above the FAB
 * - Auto-injects page context (path + main keyword) into the question
 * - Respects guest quota; non-Premium users see paywall after quota
 */

const PAGE_CONTEXT = {
  "/cepages": { fr: "vous explorez le catalogue des cépages", en: "you are browsing grape varieties" },
  "/appellations": { fr: "vous explorez les appellations", en: "you are browsing wine appellations" },
  "/explorer": { fr: "vous êtes dans l'Explorer photo", en: "you are using Photo Explorer" },
  "/carnet": { fr: "vous tenez votre carnet de dégustation", en: "you are taking tasting notes" },
  "/itineraries": { fr: "vous planifiez un itinéraire œnotouristique", en: "you are planning a wine itinerary" },
  "/nearby": { fr: "vous cherchez des producteurs autour de vous", en: "you are looking for nearby producers" },
  "/pricing": { fr: "vous regardez les abonnements", en: "you are looking at pricing" },
  "/play": { fr: "vous êtes en mode jeu", en: "you are in gaming mode" },
  "/flashcards": { fr: "vous révisez avec les flashcards", en: "you are revising with flashcards" },
  "/audrey-ai": null, // already on geoVINUM AI page, hide FAB? actually keep but as a quick assist
};

function getContext(pathname, lang) {
  // Match longest prefix
  const key = Object.keys(PAGE_CONTEXT)
    .filter((p) => pathname.startsWith(p))
    .sort((a, b) => b.length - a.length)[0];
  if (!key) return null;
  return PAGE_CONTEXT[key]?.[lang] || null;
}

export default function AudreyFAB() {
  const { lang } = useT();
  const loc = useLocation();
  const [open, setOpen] = useState(false);
  const [question, setQuestion] = useState("");
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const inputRef = useRef(null);
  const scrollRef = useRef(null);
  const sessionRef = useRef(null);

  // Hide FAB on dedicated AI surfaces (Chat home, journeys, hubs with their own composer)
  const hideOn = ["/", "/chat", "/audrey-ai", "/login", "/register"];
  const shouldHide = hideOn.some((p) => loc.pathname === p)
    || loc.pathname.startsWith("/multiplayer/")
    || loc.pathname.startsWith("/courses/")
    || loc.pathname.startsWith("/hub/");

  useEffect(() => {
    if (open) setTimeout(() => inputRef.current?.focus(), 50);
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages]);

  const send = async (e) => {
    e?.preventDefault();
    if (!question.trim() || loading) return;
    if (isGuest() && !canUse("chat")) {
      toast.error(lang === "fr" ? "Quota atteint. Inscrivez-vous gratuitement." : "Quota reached. Sign up free.");
      return;
    }
    const ctx = getContext(loc.pathname, lang);
    const q = question.trim();
    const userMsg = { role: "user", content: q };
    setMessages((m) => [...m, userMsg, { role: "assistant", content: "" }]);
    setQuestion("");
    setLoading(true);
    try {
      const augmented = ctx ? `${q}\n\n[Contexte : ${ctx} sur geoVINUM.]` : q;
      const { data } = await api.post("/audrey-ai/terroir-expert", {
        question: augmented,
        level: "amateur",
        session_id: sessionRef.current,
      });
      sessionRef.current = data.session_id;
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", content: data.answer || "…" };
        return copy;
      });
      if (isGuest()) consume("chat");
    } catch (err) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = {
          role: "assistant",
          content: lang === "fr" ? "Désolée, je suis momentanément indisponible." : "Sorry, I'm temporarily unavailable.",
        };
        return copy;
      });
    } finally {
      setLoading(false);
    }
  };

  if (shouldHide) return null;

  const ctx = getContext(loc.pathname, lang);
  const remaining = getRemaining("chat");

  return (
    <>
      {/* FAB */}
      <button
        data-testid="audrey-fab"
        onClick={() => setOpen((o) => !o)}
        className={`fixed bottom-[calc(var(--m-tabs-h)_+_0.75rem)] md:bottom-5 right-5 z-40 inline-flex items-center gap-2 pl-1.5 pr-4 py-1.5 rounded-full shadow-xl transition-all ${
          open ? "bg-[#1F3A5F] text-white" : "bg-[#E5276A] text-white hover:bg-[#C01661] hover:scale-105"
        }`}
        aria-label={lang === "fr" ? "Demander à geoVINUM AI" : "Ask geoVINUM AI"}
      >
        {open ? (
          <X className="w-5 h-5 mx-1.5 my-1" />
        ) : (
          <img src="/brand/audrey-ai.png" alt="geoVINUM AI" className="w-9 h-9 rounded-full object-cover ring-2 ring-white/80" />
        )}
        <span className="hidden sm:inline text-sm font-bold">
          {open ? (lang === "fr" ? "Fermer" : "Close") : "geoVINUM AI"}
        </span>
      </button>

      {/* Sheet */}
      {open && (
        <div
          data-testid="audrey-fab-sheet"
          className="fixed bottom-[calc(var(--m-tabs-h)_+_4.25rem)] md:bottom-24 right-5 z-40 w-[calc(100vw-2.5rem)] sm:w-[400px] max-h-[calc(100dvh_-_var(--m-tabs-h)_-_var(--safe-top)_-_5.5rem)] md:max-h-[70vh] bg-white rounded-2xl shadow-2xl border border-[#1F3A5F]/12 flex flex-col overflow-hidden"
        >
          <div className="bg-gradient-to-br from-[#E5276A] to-[#C01661] text-white px-4 py-3">
            <div className="flex items-center gap-2.5">
              <img src="/brand/audrey-ai.png" alt="geoVINUM AI" className="w-9 h-9 rounded-full object-cover ring-2 ring-white/30" />
              <h3 className="font-display text-lg leading-tight">geoVINUM AI</h3>
            </div>
            {ctx && (
              <p className="text-[11px] mt-0.5 text-white/85 italic">
                {lang === "fr" ? "Ici," : "Here,"} {ctx}.
              </p>
            )}
          </div>

          <div ref={scrollRef} className="flex-1 overflow-y-auto px-4 py-3 space-y-2 text-sm" data-testid="audrey-fab-messages">
            {messages.length === 0 && (
              <div className="text-[#4A5568] text-sm">
                <p>{lang === "fr" ? "Bonjour ! Demandez-moi tout sur le vin, ou ce qui s'affiche à l'écran." : "Hi! Ask me anything about wine or what you see on screen."}</p>
                <div className="mt-3 flex flex-wrap gap-1.5">
                  {(lang === "fr"
                    ? ["Explique ce cépage", "Quels accords pour ce vin ?", "Pourquoi cette appellation ?"]
                    : ["Explain this grape", "What pairings?", "Why this appellation?"]).map((s) => (
                    <button key={s} onClick={() => setQuestion(s)} className="px-2 py-1 rounded-full text-[11px] font-medium bg-[#FBF7F3] text-[#1F3A5F] border border-[#1F3A5F]/10 hover:border-[#E5276A]">
                      {s}
                    </button>
                  ))}
                </div>
              </div>
            )}
            {messages.map((m, i) => (
              <div key={i} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                <div className={`max-w-[85%] rounded-2xl px-3 py-2 ${
                  m.role === "user" ? "bg-[#1F3A5F] text-white" : "bg-[#FBF7F3] text-[#1F3A5F]"
                }`}>
                  {m.role === "assistant" && !m.content ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin text-[#E5276A]" />
                  ) : (
                    <p className="whitespace-pre-wrap text-[13px] leading-relaxed">{m.content}</p>
                  )}
                </div>
              </div>
            ))}
          </div>

          {isGuest() && remaining < Infinity && (
            <div className="px-4 py-1.5 bg-[#FBF7F3] text-[11px] text-[#4A5568] border-t border-[#1F3A5F]/10 flex items-center justify-between">
              <span><b className="text-[#E5276A]">{remaining}</b> {lang === "fr" ? "essais restants" : "attempts left"}</span>
              <Link to="/pricing" data-testid="audrey-fab-upgrade" className="inline-flex items-center gap-0.5 text-[#1F3A5F] font-bold underline">
                <Crown className="w-3 h-3" /> {lang === "fr" ? "Premium" : "Upgrade"}
              </Link>
            </div>
          )}

          <form onSubmit={send} className="px-3 py-2 border-t border-[#1F3A5F]/10 flex gap-2">
            <input
              ref={inputRef}
              data-testid="audrey-fab-input"
              value={question}
              onChange={(e) => setQuestion(e.target.value)}
              placeholder={lang === "fr" ? "Demandez à geoVINUM AI…" : "Ask geoVINUM AI…"}
              className="flex-1 px-3 py-2 rounded-lg border border-[#1F3A5F]/15 text-sm text-[#1F3A5F] focus:outline-none focus:border-[#E5276A]"
              disabled={loading}
            />
            <button
              data-testid="audrey-fab-send"
              type="submit"
              disabled={loading || !question.trim()}
              className="px-3 py-2 rounded-lg bg-[#E5276A] text-white hover:bg-[#C01661] disabled:opacity-50"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            </button>
          </form>
        </div>
      )}
    </>
  );
}
